import EventEmitter from "base/EventEmitter";
import $ from "jquery";
import FormInput from "base/FormInput";
import GlobalEvents from "base/GlobalEvents";

/**
 * @property {Array<FormInput>} inputs
 * @property {Object<String, FormInput>} inputsMap
 */
class FormValidator extends EventEmitter {
    constructor($form, options) {
        super();
        this.submit = this.submit.bind(this);
        $form.data("Validator", this);

        options = $.extend(true, {
            inputs: {},
            inputOptions: {},
        }, "object" === typeof options ? options : {});

        this.options = options;
        this.$form = $form;

        this.inputs = [];
        this.inputsMap = {};
        this.updateInputs();

        $form.on("submit", this.submit);

        setTimeout(() => {
            // The order of these events is important and cannot be changed
            this.emit("init", this);
            GlobalEvents.emit(EVENT_GLOBAL_ATTACH, this);
        }, 0);
    }

    /**
     * Reset the state of all form inputs to that of the initial page load
     */
    reset() {
        this.inputs.forEach((input) => {
            input.reset();
        });
    }

    updateInputs() {
        if (this.inputs.length > 0) {
            this.inputs = this.inputs.filter((input) => {
                if (input.$input.closest(this.$form).length === 1) {
                    return true;
                }

                // The input has been removed from the form, it shouldn't be validated
                input.$input.data("Validator", null);
                input.destroy();

                delete this.inputsMap[input.$input.attr("name")];

                return false;
            });
        }

        this.$form.find("input,select,textarea").filter(":not(.js-no-validate,:button,:submit,[data-no-validate])").each((_, elem) => {
            this.handleInput($(elem));
        });
    }

    handleInput($input) {
        let name = $input.attr("name");
        let input = $input.data("Validator");
        let preExisting = true;

        if (!(input instanceof FormInput)) {
            input = new FormInput($input, $.extend(true, {}, this.options.inputOptions, this.options.inputs[name]), this);
            preExisting = false;
        }

        if ('undefined' === typeof this.inputs[name] || !preExisting) {
            this.inputs.push(input);
            this.inputsMap[name] = input;

            input.on("valid", (event) => {
                this.emit("input:valid", {
                    input: input,
                    event: event,
                });
                GlobalEvents.emit(EVENT_GLOBAL_INPUT_VALID, {
                    input: input,
                    event: event,
                    form: this,
                });
            }).on("invalid", (event) => {
                this.emit("input:invalid", {
                    input: input,
                    event: event,
                });
                GlobalEvents.emit(EVENT_GLOBAL_INPUT_INVALID, {
                    input: input,
                    event: event,
                    form: this,
                });
            }).on("showMessage", (event) => {
                this.emit("input:showMessage", {
                    input: input,
                    event: event,
                });
            }).on("hideMessage", (event) => {
                this.emit("input:hideMessage", {
                    input: input,
                    event: event,
                });
            });
        }
    }

    validate(hard = true) {
        this.emit("beforeValidate");
        let valid = true;

        this.inputs.forEach((input) => {
            if (!input.validate(hard)) {
                valid = false;
            }
        });

        valid = this.emit("validate", this) && valid;

        return valid === true;
    }

    valid() {
        let valid = true;

        this.inputs.forEach((input) => {
            if (!input.valid()) {
                valid = false;
            }
        });

        return valid === true;
    }

    handleValidationException(e) {
        if (e && e.data && e.data.exception && e.data.exception === "HttpValidationException") {
            try {
                e.data.data.forEach((inputError) => {
                    let name = inputError.data.schema.name;
                    let input = this.inputsMap[name];

                    if ("undefined" === typeof input) {
                        // Handle a group of checkboxes
                        input = this.inputsMap[name + "[]"];
                    }

                    if ("undefined" === typeof input) {
                        console.warn("Invalid input returned in validation handler", name, inputError);
                        return;
                    }

                    let event = {
                        valid: false,
                        message: inputError.message,
                    };

                    if (input.emit("invalid", event)) {
                        console.log('here', input, event);
                        input.showMessage(event.message, "invalid");
                    }
                });
            } catch (e) {
                //Don't propagate error
                console.warn("Couldn't handle validation error", e);
            }
            return true;
        }

        return false;
    }

    submit() {
        if (this.validate()) {
            if (!this.emit("submit", this.$form)) {
                return true;
            }
        } else {
            this.emit("invalid", this.$form);
        }
        return false;
    }

    destroy() {
        super.destroy();
        this.$form.off("submit", this.submit).removeData("Validator");

        this.inputs.forEach((input) => input.destroy());
    }

    static attach($form, options) {
        return new FormValidator($form, options);
    }
}


export default FormValidator;
export {FormValidator, FormInput};

export const EVENT_GLOBAL_ATTACH = "FormValidator.attach";
export const EVENT_GLOBAL_INPUT_INVALID = "FormValidator.input.invalid";
export const EVENT_GLOBAL_INPUT_VALID = "FormValidator.input.valid";